import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, TriangleAlert } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import { RoutesLink } from "@/utility/RouteLinks";
import CommonFilter, { type IFilter } from "@/components/common/CommonFilter";
import TablePagination from "@/components/common/TablePagination";
import TableActions from "@/components/common/TableActions";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import { ThrowError } from "@/api/callApi";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import type { IPageInfo } from "@/interface/common.interface";
import type { ISalaryHead } from "@/interface/salaryHead.interface";
import CommonModal from "@/components/common/CommonModal";
import { Button } from "@/components/ui/button";

const SalaryHead: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<"earning" | "deduction">(
        "earning",
    );
    const [salaryHeads, setSalaryHeads] = useState<ISalaryHead[]>([]);
    console.log("salaryHeads", salaryHeads)
    const [isLoading, setIsLoading] = useState(false);

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const [filter, setFilter] = useState<IFilter>({
        search: "",
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    const toggleDeleteDialog = useCallback((id: string = "") => {
        setDeleteId(id);
        setDeleteDialogOpen((prev) => !prev);
    }, []);

    // Filter handlers
    const handleSearchChange = useCallback((value: string) => {
        setFilter((prev) => ({ ...prev, search: value }));
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }, []);

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
    };


    // Fetch data
    const fetchData = async (
        page = paginationData.currentPage,
        limit = paginationData.pageSize,
    ) => {
        setIsLoading(true);
        try {
            const type = activeTab === "earning" ? "earning" : "deduction";

            const apiFilter = {
                ...filter,
                type,
            };

            const paramsURL = GetFilterUrl(apiFilter);
            const PATH = `pageNumber=${page}&pageSize=${limit}${paramsURL ? `&${paramsURL}` : ""}`;

            const response = await SalaryHeadServices.getList(PATH);


            if (response.status && response.data) {
                setSalaryHeads(response.data.salaryHead || []);
                setPaginationData((prev) => ({
                    ...prev,
                    total: response.data.pageInfo?.total || 0,
                    totalPages: response.data.pageInfo?.totalPages || 0,
                }));
            }
        } catch (err) {
            ThrowError(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = useCallback(async () => {
        if (!deleteId) return;
        setIsLoading(true);
        try {
            const response = await SalaryHeadServices.delete(deleteId);
            if (response.status) {
                showToastNotification("success", response.message || "Salary head deleted successfully");
                fetchData();
            } else {
                showToastNotification("danger", response.message || "Failed to delete salary head");
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
            toggleDeleteDialog("");
        }
    }, [deleteId, toggleDeleteDialog, fetchData]);



    useEffect(() => {
        fetchData();
    }, [
        paginationData.currentPage,
        paginationData.pageSize,
        filter.search,
        activeTab,
    ]);

    // Table columns
    const columns: TableColumn<ISalaryHead>[] = [
        {
            key: "sNo",
            header: "S.No",
            render: (_item, index) => (
                <span className="text-muted-foreground font-medium text-sm">
                    {String(
                        (paginationData.currentPage - 1) * paginationData.pageSize +
                        index +
                        1,
                    ).padStart(2, "0")}
                </span>
            ),
        },
        {
            key: "name",
            header: "Name",
            render: (item) => <span className="capitalize">{item?.name || "—"}</span>,
        },
        {
            key: "type",
            header: "Type",
            render: (item) => <span className="capitalize">{item?.type || "—"}</span>,
        },
        {
            key: "amountType",
            header: "Amount Type",
            render: (item) => <span className="capitalize">{item?.amountType || "—"}</span>,
        },
        {
            key: "fixVariable",
            header: "Fixed/Variable",
            render: (item) => <span className="capitalize">{item?.fixVariable || "—"}</span>,
        },
        {
            key: "frequency",
            header: "Frequency",
            render: (item) => <span className="capitalize">{item?.frequency || "—"}</span>,
        },
        {
            key: "payableIn",
            header: "Payable In",
            render: (item) => <span className="capitalize">{item?.payableIn || "—"}</span>,
        },
        {
            key: "partOfForm16",
            header: "Part of Form 16",
            render: (item) => <span className="capitalize">{item?.partOfForm16 === true ? "Yes" : "No"}</span>,
        },
        {
            key: "maxCap",
            header: "Max Cap",
            render: (item) => <span className="capitalize">{item?.maxCap || "—"}</span>,
        },

        {
            key: "wantToShowInOfferLetter",
            header: "Show in Offer Letter",
            render: (item) => (
                <span>{item?.wantToShowInOfferLetter ? "Yes" : "No"}</span>
            ),
        },
        {
            key: "fromEmployeer",
            header: "From Employer",
            render: (item) => <span>{item?.fromEmployeer ? "Yes" : "No"}</span>,
        },
        {
            key: "isBase",
            header: "Is Base",
            render: (item) => <span>{item?.isBase === true ? "Yes" : "No"}</span>,
        },
        {
            key: "isActive",
            header: "Active",

            render: (item) => <span className={item?.isActive === true ? "text-green-600 font-medium" : "text-red-600"}>{item?.isActive === true ? "Active" : "Inactive"}</span>,
        },

        {
            key: "actions",
            header: "Actions",
            render: (item: ISalaryHead) => {
                const isBase = item?.isBase === true;

                return (
                    <TableActions
                        onView={() => {
                            if (item?.id)
                                navigate(`${RoutesLink.SalaryHeadDetails}/${item.id}`);
                        }}
                        actions={[
                            {
                                label: "Edit",
                                onClick: () => navigate(`${RoutesLink.editSalaryHead}/${item.id}`),
                                icon: <Pencil className="h-4 w-4" />,
                                show: !isBase,
                            },
                        ]}
                        viewLabel="View Details"
                        onDelete={!isBase ? () => toggleDeleteDialog(item.id) : undefined}
                    />
                );
            },
        }
    ];

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="Salary Head"
                    buttonText="Add Salary Head"
                    buttonIcon={<Plus />}
                    isBackButtonVisible={true}
                    onClick={() => navigate(RoutesLink.addSalaryHead)}
                />

                <Tabs
                    value={activeTab}
                    onValueChange={(val) => setActiveTab(val as "earning" | "deduction")}
                    className="w-full"
                >
                    <TabsList className="bg-slate-100/80 flex py-5 flex-nowrap overflow-x-auto overflow-y-hidden h-fit gap-2 rounded-lg border border-slate-200/60 shadow-inner mb-3 scroll-bar-hidden items-center">
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white"
                            value="earning"
                        >
                            Earnings
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white"
                            value="deduction"
                        >
                            Deductions
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="earning">
                        <div className="space-y-4">
                            <CommonFilter
                                searchValue={filter.search || ""}
                                onSearchChange={handleSearchChange}
                                NoAdvanceFilters={true}
                            />
                            <CommonTable
                                columns={columns}
                                data={salaryHeads}
                                loading={isLoading}
                                stickyColumns={2}
                            />
                            <TablePagination
                                paginationData={paginationData}
                                setPaginationData={setPaginationData}
                                handleChangePageSize={handleChangePageSize}
                                isLoading={isLoading}
                            />
                        </div>
                    </TabsContent>

                    <TabsContent value="deduction">
                        <div className="space-y-4">
                            <CommonFilter
                                searchValue={filter.search || ""}
                                onSearchChange={handleSearchChange}
                                NoAdvanceFilters={true}
                            />
                            <CommonTable
                                columns={columns}
                                data={salaryHeads}
                                loading={isLoading}
                            />
                            <TablePagination
                                paginationData={paginationData}
                                setPaginationData={setPaginationData}
                                handleChangePageSize={handleChangePageSize}
                                isLoading={isLoading}
                            />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <CommonModal
                show={deleteDialogOpen}
                onClose={() => toggleDeleteDialog("")}
                title=""
                className="max-w-md rounded-2xl p-0 overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <TriangleAlert className="text-red-600 w-8 h-8" />
                        </div>
                        <h2 className="mt-5 text-2xl font-bold text-gray-900">Delete Salary Head?</h2>
                        <p className="mt-3 text-sm leading-6 text-gray-500">
                            This action cannot be undone. Once deleted, the salary head will be permanently removed.
                        </p>
                    </div>
                    <div className="mt-8 flex gap-3">
                        <Button variant="outline" className="flex-1" onClick={() => toggleDeleteDialog("")}>
                            Cancel
                        </Button>
                        <Button variant="destructive" className="flex-1" onClick={handleDelete}>
                            Delete
                        </Button>
                    </div>
                </div>
            </CommonModal>
        </Layout>
    );
};

export default SalaryHead;
